import React, { useState, useEffect, useContext } from 'react';
import { firestore } from '../../firebase/config';
import { BookingContext } from '../../context/BookingContext';
import './Calendar.css';

const Calendar = ({ practitionerId, onDateSelect }) => {
  const { setCurrentStep, updateBookingData, bookingData } = useContext(BookingContext);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [calendarDays, setCalendarDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // États pour les disponibilités
  const [availabilities, setAvailabilities] = useState({});
  const [vacationDates, setVacationDates] = useState([]);

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Charger les données de disponibilités et congés
  useEffect(() => {
    const loadPractitionerAvailability = async () => {
      if (!practitionerId) {
        setError("Aucun praticien sélectionné.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Générer les jours du calendrier
        generateCalendarDays();

        // Récupérer les disponibilités par défaut du praticien
        const defaultAvailabilitiesSnapshot = await firestore
          .collection('availability')
          .where('practitionerId', '==', practitionerId)
          .where('type', '==', 'default')
          .get();

        // Récupérer les disponibilités spécifiques pour un intervalle étendu
        const firstDayPrevMonth = new Date(currentYear, currentMonth - 1, 1);
        const lastDayNextMonth = new Date(currentYear, currentMonth + 2, 0);

        const startDateStr = firstDayPrevMonth.toISOString().split('T')[0];
        const endDateStr = lastDayNextMonth.toISOString().split('T')[0];

        const specificAvailabilitiesSnapshot = await firestore
          .collection('availability')
          .where('practitionerId', '==', practitionerId)
          .where('type', '==', 'specific')
          .where('date', '>=', startDateStr)
          .where('date', '<=', endDateStr)
          .get();

        // Récupérer les congés complets
        const vacationsSnapshot = await firestore
          .collection('vacations')
          .where('practitionerId', '==', practitionerId)
          .where('isFullDay', '==', true)
          .get();

        console.log("Disponibilités par défaut récupérées:", defaultAvailabilitiesSnapshot.size);
        console.log("Disponibilités spécifiques récupérées:", specificAvailabilitiesSnapshot.size);
        console.log("Congés récupérés:", vacationsSnapshot.size);

        // Ajouter des logs détaillés pour déboguer
        console.log("=== DEBUG DISPONIBILITÉS ===");
        defaultAvailabilitiesSnapshot.docs.forEach(doc => {
          console.log("Disponibilité par défaut:", doc.data());
        });
        specificAvailabilitiesSnapshot.docs.forEach(doc => {
          console.log("Disponibilité spécifique:", doc.data());
        });
        console.log("=== FIN DEBUG ===");

        // Traiter les disponibilités par défaut (hebdomadaires)
        const defaultAvailabilities = {};
        if (defaultAvailabilitiesSnapshot.empty) {
          // Paramètres par défaut si aucune configuration
          [0, 1, 2, 3, 4].forEach(day => {
            defaultAvailabilities[day] = { isAvailable: true };
          });
          [5, 6].forEach(day => {
            defaultAvailabilities[day] = { isAvailable: false };
          });
          console.log("Aucune disponibilité trouvée, utilisation des paramètres par défaut");
        } else {
          defaultAvailabilitiesSnapshot.docs.forEach(doc => {
            //const data = migrateAvailabilityData(doc.data()); // Appliquer la migration
            const data = doc.data();
            const dayOfWeek = data.day !== undefined ? data.day : data.dayOfWeek;
            const isAvailable = checkDayAvailability(data);
            defaultAvailabilities[dayOfWeek] = { isAvailable };
            console.log(`Jour ${dayOfWeek}: ${isAvailable ? 'disponible' : 'indisponible'}`, data);
          });
        }

        // Traiter les disponibilités spécifiques
        const specificAvailabilities = {};
        specificAvailabilitiesSnapshot.docs.forEach(doc => {
          //const data = migrateAvailabilityData(doc.data()); // Appliquer la migration
          const data = doc.data();
          const isAvailable = checkDayAvailability(data);
          specificAvailabilities[data.date] = { isAvailable };
          console.log(`Date spécifique ${data.date}: ${isAvailable ? 'disponible' : 'indisponible'}`);
        });


        // Traiter les congés
        const vacationDatesArray = [];
        vacationsSnapshot.docs.forEach(doc => {
          const vacation = doc.data();
          const startDate = new Date(vacation.startDate);
          const endDate = new Date(vacation.endDate);

          // Ajouter toutes les dates entre startDate et endDate
          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            vacationDatesArray.push(d.toISOString().split('T')[0]);
          }
        });

        setAvailabilities({
          default: defaultAvailabilities,
          specific: specificAvailabilities
        });
        setVacationDates(vacationDatesArray);
        setLoading(false);

      } catch (error) {
        console.error('Erreur lors du chargement des disponibilités:', error);
        setError("Erreur lors du chargement des disponibilités.");
        setLoading(false);
      }
    };

    loadPractitionerAvailability();
  }, [currentMonth, currentYear, practitionerId]);

  // Fonction pour vérifier si un jour est disponible selon les données de disponibilité
  const checkDayAvailability = (availabilityData) => {
    console.log("Vérification disponibilité pour:", availabilityData);
    console.log("Mode de consultation demandé:", bookingData?.consultationMode);

    // Si le jour n'est pas un jour de travail
    if (availabilityData.isWorkingDay === false) {
      console.log("Jour non travaillé (isWorkingDay=false)");
      return false;
    }

    const consultationMode = bookingData?.consultationMode || 'cabinet';
    console.log("Mode de consultation utilisé pour la vérification:", consultationMode);

    // Vérifier selon le mode (demi-journée ou journée complète)
    if (availabilityData.inputMode === 'half-day') {
      const morningAvailable = availabilityData.morningAvailable &&
        checkModeCompatibility(availabilityData, consultationMode, 'morning');
      const afternoonAvailable = availabilityData.afternoonAvailable &&
        checkModeCompatibility(availabilityData, consultationMode, 'afternoon');

      const available = morningAvailable || afternoonAvailable;
      console.log("Mode demi-journée:", {
        morningAvailable: availabilityData.morningAvailable,
        morningModeOk: checkModeCompatibility(availabilityData, consultationMode, 'morning'),
        afternoonAvailable: availabilityData.afternoonAvailable,
        afternoonModeOk: checkModeCompatibility(availabilityData, consultationMode, 'afternoon'),
        result: available
      });
      return available;
    } else if (availabilityData.inputMode === 'full-day') {
      const available = availabilityData.fullDayAvailable &&
        checkModeCompatibility(availabilityData, consultationMode, 'fullDay');
      console.log("Mode journée complète:", {
        fullDayAvailable: availabilityData.fullDayAvailable,
        modeOk: checkModeCompatibility(availabilityData, consultationMode, 'fullDay'),
        result: available
      });
      return available;
    } else {
      // Mode legacy - vérifier isWorkingDay et compatibilité mode
      const available = availabilityData.isWorkingDay !== false &&
        checkModeCompatibility(availabilityData, consultationMode, 'legacy');
      console.log("Mode legacy:", {
        isWorkingDay: availabilityData.isWorkingDay,
        modeOk: checkModeCompatibility(availabilityData, consultationMode, 'legacy'),
        result: available
      });
      return available;
    }
  };

  // Fonction pour vérifier la compatibilité du mode de consultation
  const checkModeCompatibility = (availabilityData, consultationMode, period) => {
    console.log(`Vérification compatibilité mode ${consultationMode} pour période ${period}:`, {
      morningAllowsCabinet: availabilityData.morningAllowsCabinet,
      afternoonAllowsCabinet: availabilityData.afternoonAllowsCabinet,
      fullDayAllowsCabinet: availabilityData.fullDayAllowsCabinet,
      morningAllowsRemote: availabilityData.morningAllowsRemote,
      morningAllowsHomeVisits: availabilityData.morningAllowsHomeVisits,
      afternoonAllowsRemote: availabilityData.afternoonAllowsRemote,
      afternoonAllowsHomeVisits: availabilityData.afternoonAllowsHomeVisits,
      fullDayAllowsRemote: availabilityData.fullDayAllowsRemote,
      fullDayAllowsHomeVisits: availabilityData.fullDayAllowsHomeVisits,
      allowsRemote: availabilityData.allowsRemote,
      allowsHomeVisits: availabilityData.allowsHomeVisits
    });

    switch (consultationMode) {
      case 'cabinet':
        if (period === 'morning') {
          // Vérifier morningAllowsCabinet avec fallback pour compatibilité
          const result = availabilityData.morningAllowsCabinet !== false; // Par défaut true si pas défini
          console.log(`Cabinet matin: ${availabilityData.morningAllowsCabinet} (avec fallback) = ${result}`);
          return result;
        } else if (period === 'afternoon') {
          const result = availabilityData.afternoonAllowsCabinet !== false;
          console.log(`Cabinet après-midi: ${availabilityData.afternoonAllowsCabinet} (avec fallback) = ${result}`);
          return result;
        } else if (period === 'fullDay') {
          const result = availabilityData.fullDayAllowsCabinet !== false;
          console.log(`Cabinet journée complète: ${availabilityData.fullDayAllowsCabinet} (avec fallback) = ${result}`);
          return result;
        } else {
          // Mode legacy - Cabinet disponible par défaut pour compatibilité ascendante
          const result = true;
          console.log(`Cabinet legacy: ${result}`);
          return result;
        }

      case 'remote':
        if (period === 'morning') {
          const result = availabilityData.morningAllowsRemote;
          console.log(`Remote matin: ${availabilityData.morningAllowsRemote} = ${result}`);
          return result;
        } else if (period === 'afternoon') {
          const result = availabilityData.afternoonAllowsRemote;
          console.log(`Remote après-midi: ${availabilityData.afternoonAllowsRemote} = ${result}`);
          return result;
        } else if (period === 'fullDay') {
          const result = availabilityData.fullDayAllowsRemote;
          console.log(`Remote journée complète: ${availabilityData.fullDayAllowsRemote} = ${result}`);
          return result;
        } else {
          // Mode legacy
          const result = availabilityData.allowsRemote;
          console.log(`Remote legacy: ${result}`);
          return result;
        }

      case 'home':
        if (period === 'morning') {
          const result = availabilityData.morningAllowsHomeVisits;
          console.log(`Home matin: ${availabilityData.morningAllowsHomeVisits} = ${result}`);
          return result;
        } else if (period === 'afternoon') {
          const result = availabilityData.afternoonAllowsHomeVisits;
          console.log(`Home après-midi: ${availabilityData.afternoonAllowsHomeVisits} = ${result}`);
          return result;
        } else if (period === 'fullDay') {
          const result = availabilityData.fullDayAllowsHomeVisits;
          console.log(`Home journée complète: ${availabilityData.fullDayAllowsHomeVisits} = ${result}`);
          return result;
        } else {
          // Mode legacy
          const result = availabilityData.allowsHomeVisits;
          console.log(`Home legacy: ${result}`);
          return result;
        }

      default:
        console.log(`Mode inconnu: ${consultationMode}, autorisation par défaut`);
        return true; // Mode inconnu, on autorise par défaut
    }
  };

  // Ajouter une fonction utilitaire pour gérer la compatibilité ascendante
  const getMigrationCompatibleValue = (availabilityData, field) => {
    // Si le nouveau champ existe, l'utiliser
    if (availabilityData[field] !== undefined) {
      return availabilityData[field];
    }

    // Sinon, utiliser des valeurs par défaut selon le champ
    if (field.includes('AllowsCabinet')) {
      return true; // Cabinet activé par défaut pour compatibilité
    }

    return false; // Remote et Home désactivés par défaut
  };

  // Générer les jours du calendrier
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    const firstDayOfWeek = firstDayOfMonth.getDay();
    const daysFromPrevMonth = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    const days = [];

    // Jours du mois précédent
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();

    for (let i = 0; i < daysFromPrevMonth; i++) {
      const day = daysInPrevMonth - daysFromPrevMonth + i + 1;
      const date = new Date(prevMonthYear, prevMonth, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isPast = date < today;
      const dayOfWeek = date.getDay();
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      days.push({
        day,
        month: prevMonth,
        year: prevMonthYear,
        currentMonth: false,
        isPast,
        dayOfWeek: adjustedDayOfWeek,
        disabled: isPast
      });
    }

    // Jours du mois actuel
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth, i);
      const isPast = date < today;
      const dayOfWeek = date.getDay();
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      days.push({
        day: i,
        month: currentMonth,
        year: currentYear,
        currentMonth: true,
        isPast,
        dayOfWeek: adjustedDayOfWeek,
        disabled: isPast
      });
    }

    // Jours du mois suivant
    const totalDaysAdded = days.length;
    const daysFromNextMonth = Math.ceil(totalDaysAdded / 7) * 7 - totalDaysAdded;

    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    for (let i = 1; i <= daysFromNextMonth; i++) {
      const date = new Date(nextMonthYear, nextMonth, i);
      const isPast = date < today;
      const dayOfWeek = date.getDay();
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      days.push({
        day: i,
        month: nextMonth,
        year: nextMonthYear,
        currentMonth: false,
        isPast,
        dayOfWeek: adjustedDayOfWeek,
        disabled: isPast
      });
    }

    setCalendarDays(days);
  };

  // Vérifier si un jour est disponible
  const isDayAvailable = (day) => {
    // Si le jour est dans le passé, il n'est pas disponible
    if (day.disabled || day.isPast) {
      return false;
    }

    const year = day.year;
    const month = String(day.month + 1).padStart(2, '0');
    const date = String(day.day).padStart(2, '0');
    const dateStr = `${year}-${month}-${date}`;

    console.log(`Vérification disponibilité pour ${dateStr}, jour de la semaine: ${day.dayOfWeek}`);

    // Vérifier les congés complets
    if (vacationDates.includes(dateStr)) {
      console.log(`${dateStr}: en congé`);
      return false;
    }

    // Vérifier les disponibilités spécifiques en premier
    if (availabilities.specific && availabilities.specific[dateStr]) {
      const result = availabilities.specific[dateStr].isAvailable;
      console.log(`${dateStr}: disponibilité spécifique = ${result}`);
      return result;
    }

    // Utiliser les disponibilités par défaut
    if (availabilities.default && availabilities.default[day.dayOfWeek] !== undefined) {
      const result = availabilities.default[day.dayOfWeek].isAvailable;
      console.log(`${dateStr}: disponibilité par défaut jour ${day.dayOfWeek} = ${result}`);
      return result;
    }

    // Par défaut, considérer comme indisponible
    console.log(`${dateStr}: aucune disponibilité trouvée, considéré comme indisponible`);
    return false;
  };

  // Sélectionner un jour
  const selectDay = (day) => {
    if (!isDayAvailable(day)) {
      console.log("Jour non disponible:", day);
      return;
    }

    const selectedDate = new Date(day.year, day.month, day.day, 12, 0, 0);
    console.log("Date sélectionnée:", selectedDate);

    setSelectedDay(day);

    // Mettre à jour la date formatée pour l'affichage
    const formattedDate = selectedDate.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // Mettre à jour les données de réservation
    updateBookingData({
      date: selectedDate,
      formattedDate: formattedDate,
      time: null, // Réinitialiser l'heure quand on change de date
      consultationMode: bookingData?.consultationMode || 'cabinet'
    });

    // Notifier le composant parent
    if (onDateSelect) {
      onDateSelect(selectedDate);
    }
  };

  // Fonction pour passer à l'étape suivante
  const handleNext = () => {
    if (selectedDay) {
      setCurrentStep(4); // Aller à l'étape de sélection des créneaux horaires
    }
  };

  // Passer au mois précédent
  const prevMonth = () => {
    setSelectedDay(null);
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Passer au mois suivant
  const nextMonth = () => {
    setSelectedDay(null);
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Obtenir la classe CSS pour un jour
  const getDayClass = (day) => {
    let className = 'calendar-day';

    if (!day.currentMonth) {
      className += ' other-month';
    }

    if (day.isPast || day.disabled) {
      className += ' past-day';
    } else if (isDayAvailable(day)) {
      className += ' available-day';
    } else {
      className += ' unavailable-day';
    }

    if (selectedDay &&
      selectedDay.day === day.day &&
      selectedDay.month === day.month &&
      selectedDay.year === day.year) {
      className += ' selected';
    }

    return className;
  };

  if (loading) {
    return (
      <div className="calendar-container">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i>
          Chargement des disponibilités...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendar-container">
        <div className="error">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
        <div className="step-navigation">
          <button
            className="btn btn-back"
            onClick={() => setCurrentStep(2)}
          >
            Retour
          </button>
          <div></div> {/* Placeholder pour maintenir l'espacement */}
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <h2>Choisissez une date</h2>

      {/* Affichage du mode de consultation */}
      {bookingData?.consultationMode && (
        <div className="consultation-mode-reminder">
          <span className={`mode-badge ${bookingData.consultationMode}`}>
            {bookingData.consultationMode === 'cabinet' && '🏢 Consultation en cabinet'}
            {bookingData.consultationMode === 'remote' && '💻 Consultation à distance'}
            {bookingData.consultationMode === 'home' && '🏠 Consultation à domicile'}
          </span>
        </div>
      )}

      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={prevMonth}>
          <i className="fas fa-chevron-left"></i>
        </button>
        <h3 className="calendar-title">
          {monthNames[currentMonth]} {currentYear}
        </h3>
        <button className="calendar-nav-btn" onClick={nextMonth}>
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>

      <div className="calendar-grid">
        <div className="calendar-weekdays">
          <div className="weekday">Lun</div>
          <div className="weekday">Mar</div>
          <div className="weekday">Mer</div>
          <div className="weekday">Jeu</div>
          <div className="weekday">Ven</div>
          <div className="weekday">Sam</div>
          <div className="weekday">Dim</div>
        </div>

        <div className="calendar-days">
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={getDayClass(day)}
              onClick={() => selectDay(day)}
            >
              {day.day}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation avec bouton Suivant */}
      <div className="step-navigation">
        <button
          className="btn btn-back"
          onClick={() => setCurrentStep(2)}
        >
          Retour
        </button>
        <button
          className={`btn ${selectedDay ? 'btn-primary' : 'btn-disabled'}`}
          onClick={handleNext}
          disabled={!selectedDay}
        >
          Suivant
        </button>
      </div>
    </div>
  );
};

export default Calendar;